import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  Bold, Italic, Strikethrough, Link2, List, ListOrdered,
  Undo2, Redo2, Indent, Outdent, Camera, ImagePlus, Loader2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { customFetch } from "@workspace/api-client-react";
import { compressImage } from "@/lib/image-compress";

// ─── Colour swatches ────────────────────────────────────────────────────────
const HIGHLIGHT_COLORS = [
  "#FFF176", "#A5D6A7", "#90CAF9", "#F48FB1",
  "#FFE0B2", "#CE93D8", "#80DEEA", "#FFFFFF",
];
const TEXT_COLORS = [
  "#000000", "#1a73e8", "#e53935", "#43a047",
  "#f57c00", "#8e24aa", "#00838f", "#6d4c41",
];

export interface InsertAPI {
  append: (html: string) => void;
  replace: (html: string) => void;
  isEmpty: () => boolean;
}

interface RichEditorProps {
  content: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  className?: string;
  compact?: boolean;
  insertRef?: { current: InsertAPI | null };
  /** Additional buttons rendered at the end of the toolbar (after the image
   *  buttons). Use for context-specific actions like "Pull from program". */
  extraToolbarItems?: React.ReactNode;
}

// ─── Task #148: image-removal tracking helpers ─────────────────────────────
// The rich editor uploads images to S3 the moment they're inserted. To
// keep `image_storage_bytes` honest we have to detect when the teacher
// later deletes one of those images (cut, backspace, drag-out, undo
// past insertion) and tell the server to refund the bytes.

const STORED_IMAGE_PREFIX = "/api/storage/objects/";

function srcToObjectPath(src: string): string | null {
  if (!src.startsWith(STORED_IMAGE_PREFIX)) return null;
  return src.slice("/api/storage".length);
}

function extractStoredImageSrcs(html: string | undefined | null): Set<string> {
  const out = new Set<string>();
  if (!html) return out;
  // Cheap regex over the HTML string — Tiptap wraps stored images in
  // standard <img src="…"> tags. The set discards duplicates so a paste
  // that re-uses an existing src counts once.
  const re = /<img[^>]+src=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const src = m[1];
    if (src.startsWith(STORED_IMAGE_PREFIX)) out.add(src);
  }
  return out;
}

function collectStoredImageSrcsFromEditor(editor: { state: { doc: { descendants: (cb: (n: { type: { name: string }; attrs: Record<string, unknown> }) => void) => void } } }): Set<string> {
  const out = new Set<string>();
  editor.state.doc.descendants((node) => {
    if (node.type.name === "image") {
      const src = node.attrs.src;
      if (typeof src === "string" && src.startsWith(STORED_IMAGE_PREFIX)) out.add(src);
    }
  });
  return out;
}

async function uploadImageToStorage(rawFile: File): Promise<string> {
  // Task #138 — compress + EXIF-strip in the browser before requesting a
  // presigned URL. Keeps S3 spend bounded (1.5 MB cap matches the
  // server-side gate in routes/storage.ts) and prevents the original
  // unedited photo (with embedded location etc.) from leaving the device.
  const file = await compressImage(rawFile);

  const { uploadURL, objectPath } = await customFetch<{ uploadURL: string; objectPath: string }>("/api/storage/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: file.name,
      size: file.size,
      contentType: file.type || "image/jpeg",
    }),
  });

  const uploadRes = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "image/jpeg" },
  });
  if (!uploadRes.ok) throw new Error("Upload failed");

  // Task #138 — confirm AFTER the PUT succeeds so per-teacher
  // image_storage_bytes only counts bytes actually in S3 (no
  // double-count on retries / aborts).
  try {
    await customFetch("/api/storage/uploads/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ size: file.size }),
    });
  } catch {
    // best-effort — file is in S3, accounting is non-critical for the user
  }

  return `/api/storage${objectPath}`;
}

// ─── StorageImageView ────────────────────────────────────────────────────────
// Custom Tiptap NodeView for image nodes. When the src is a private storage
// URL (/api/storage/objects/…), the browser's <img> tag cannot authenticate
// (it sends cookies, not the Authorization: Bearer header that Clerk needs).
// This view fetches the image data through customFetch (which carries the
// Bearer token), converts it to a blob URL, and renders that instead.
// The original src is preserved in the ProseMirror document (serialised to
// HTML), so saved content remains portable — only the runtime render changes.
function StorageImageView({ node, selected }: {
  node: { attrs: Record<string, unknown> };
  selected: boolean;
}) {
  const src = (node.attrs.src as string) ?? "";
  const alt = (node.attrs.alt as string) ?? "";
  const isStorageSrc = src.startsWith(STORED_IMAGE_PREFIX);

  const [blobSrc, setBlobSrc] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const activeBlobUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!isStorageSrc) return;
    let cancelled = false;
    setLoadError(false);
    setBlobSrc(null);

    customFetch<Blob>(src, { responseType: "blob" })
      .then((blob) => {
        if (cancelled) return;
        if (activeBlobUrl.current) URL.revokeObjectURL(activeBlobUrl.current);
        const url = URL.createObjectURL(blob);
        activeBlobUrl.current = url;
        setBlobSrc(url);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => { cancelled = true; };
  }, [src, isStorageSrc]);

  useEffect(() => {
    return () => {
      if (activeBlobUrl.current) {
        URL.revokeObjectURL(activeBlobUrl.current);
        activeBlobUrl.current = null;
      }
    };
  }, []);

  const effectiveSrc = isStorageSrc ? blobSrc : src;

  return (
    <NodeViewWrapper>
      {loadError ? (
        <span className="text-xs text-gray-400 italic px-1">[Image failed to load]</span>
      ) : effectiveSrc ? (
        <img
          src={effectiveSrc}
          alt={alt}
          draggable="true"
          data-drag-handle
          style={{
            maxWidth: "100%",
            outline: selected ? "2px solid #6366f1" : "none",
            outlineOffset: selected ? "2px" : undefined,
          }}
        />
      ) : isStorageSrc ? (
        <span className="text-xs text-gray-400 italic px-1">Loading image…</span>
      ) : null}
    </NodeViewWrapper>
  );
}

export function RichEditor({ content, onChange, placeholder, minHeight = 140, className, compact = false, insertRef, extraToolbarItems }: RichEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [imageUploading, setImageUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Stable ref so the ProseMirror plugin (created once in useMemo) can always
  // call the latest insertImageFile callback without needing to be recreated.
  const insertImageFileRef = useRef<((file: File) => void) | null>(null);

  // Extend Image to add a HIGH-PRIORITY ProseMirror plugin that intercepts
  // dropped/pasted image files BEFORE the Image extension (or browser) can
  // insert them as broken base64 placeholders. The plugin calls our upload
  // handler via the stable ref and returns true (consumed) for image files,
  // leaving non-image drops/pastes to fall through to normal handling.
  const ImageWithUpload = useMemo(() => Image.extend({
    // Render every image node through StorageImageView. For private storage
    // URLs (/api/storage/objects/…) it fetches with the Bearer token and
    // shows a blob URL; for other URLs it renders the src directly.
    addNodeView() {
      return ReactNodeViewRenderer(StorageImageView);
    },
    addProseMirrorPlugins() {
      const parentPlugins = this.parent?.() ?? [];
      return [
        new Plugin({
          key: new PluginKey("imageFileUpload"),
          props: {
            handleDrop(_view: unknown, event: Event): boolean {
              const files = Array.from(
                (event as DragEvent).dataTransfer?.files ?? []
              ).filter((f: File) => f.type.startsWith("image/"));
              if (!files.length) return false;
              event.preventDefault();
              files.forEach((f: File) => insertImageFileRef.current?.(f));
              return true;
            },
            handlePaste(_view: unknown, event: Event): boolean {
              const items = Array.from(
                (event as ClipboardEvent).clipboardData?.items ?? []
              ).filter((i: DataTransferItem) => i.type.startsWith("image/"));
              if (!items.length) return false;
              event.preventDefault();
              items.forEach((item: DataTransferItem) => {
                const file = item.getAsFile();
                if (file) insertImageFileRef.current?.(file);
              });
              return true;
            },
          },
        }),
        ...parentPlugins,
      ];
    },
  }), []);

  // Task #148 — track the set of uploaded-image src values currently in
  // the document so we can detect when the teacher deletes an image and
  // refund the per-teacher image_storage_bytes counter. Only paths under
  // `/api/storage/objects/...` (i.e. actually stored in S3) are tracked;
  // base64 fallbacks are ignored. Initialised from the prop content so
  // the first onUpdate after mount doesn't think every existing image
  // was just removed.
  const trackedImageSrcs = useRef<Set<string>>(extractStoredImageSrcs(content));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      // ImageWithUpload extends Image and embeds a high-priority plugin that
      // intercepts file drops/pastes before any base64 insertion can occur.
      ImageWithUpload.configure({ inline: false, allowBase64: true }),
    ],
    content: content || "",
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none p-4 min-h-[${minHeight}px] text-sm text-gray-700`,
        style: `min-height:${minHeight}px`,
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor }) => {
      const currentSrcs = collectStoredImageSrcsFromEditor(editor);
      const removed: string[] = [];
      trackedImageSrcs.current.forEach((src) => {
        if (!currentSrcs.has(src)) removed.push(src);
      });
      if (removed.length > 0) {
        const objectPaths = removed
          .map(srcToObjectPath)
          .filter((p): p is string => p != null);
        if (objectPaths.length > 0) {
          // Best-effort — failure just leaves the operator counter
          // slightly inflated; the optional reconciliation script
          // re-anchors from truth.
          customFetch("/api/storage/uploads/refund", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ objectPaths }),
          }).catch(() => { /* best-effort */ });
        }
      }
      trackedImageSrcs.current = currentSrcs;
      onChangeRef.current?.(editor.getHTML());
    },
  });

  const lastContent = useRef(content);
  useEffect(() => {
    if (!editor) return;
    if (content === lastContent.current) return;
    lastContent.current = content;
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content || "", { emitUpdate: false });
      // Task #148 — re-anchor the tracker so a parent-driven content
      // swap (e.g. switching between diary entries) doesn't trigger a
      // spurious refund for images that just appeared from the new prop.
      trackedImageSrcs.current = extractStoredImageSrcs(content);
    }
  }, [editor, content]);

  const insertImageFile = useCallback(async (file: File) => {
    if (!editor) return;
    setImageUploading(true);
    try {
      const src = await uploadImageToStorage(file);
      editor.chain().focus().setImage({ src }).run();
    } catch {
      // Silent fail — no base64 fallback. The ProseMirror plugin already
      // prevented any placeholder from being inserted, so the editor is clean.
    } finally {
      setImageUploading(false);
    }
  }, [editor]);

  // Keep the stable ref in sync with the latest insertImageFile so the
  // ProseMirror plugin (created once in useMemo) always calls current logic.
  useEffect(() => {
    insertImageFileRef.current = insertImageFile;
  }, [insertImageFile]);

  const handleFileInput = useCallback((files: FileList | null) => {
    if (!files || !editor) return;
    Array.from(files).forEach((f) => { if (f.type.startsWith("image/")) insertImageFile(f); });
  }, [editor, insertImageFile]);

  if (!editor) return null;

  // Wire up the imperative insert API used by ProgramItemPicker (Stage 3C.2).
  // Assigned on every render so the closures stay fresh; editor is stable.
  if (insertRef) {
    insertRef.current = {
      append:  (html) => editor.chain().focus().insertContent(html).run(),
      replace: (html) => editor.commands.setContent(html, { emitUpdate: true }),
      isEmpty: ()     => !editor.getText().trim(),
    };
  }

  const ToolbarButton = ({
    onClick, active, title, children, disabled,
  }: { onClick: () => void; active?: boolean; title: string; children: React.ReactNode; disabled?: boolean }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
      title={title}
      disabled={disabled}
      className={`h-7 w-7 flex items-center justify-center rounded text-sm transition-colors
        ${active ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}
        ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );

  const setLink = () => {
    const url = window.prompt("URL:", editor.getAttributes("link").href ?? "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className={`border-0 overflow-hidden ${className ?? ""}`}>
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => { handleFileInput(e.target.files); e.target.value = ""; }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { handleFileInput(e.target.files); e.target.value = ""; }}
      />

      {/* Toolbar — hidden in compact mode */}
      <div className={`flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap${compact ? " hidden" : ""}`}>
        {/* Undo / Redo */}
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo" active={false}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo" active={false}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Bold / Italic / Strike */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Highlight */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Highlight"
              className="h-7 px-1.5 flex items-center gap-0.5 rounded text-gray-600 hover:bg-gray-100 text-xs font-medium"
            >
              <span className="relative">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                <span className="absolute -bottom-0.5 left-0 right-0 h-1 rounded" style={{ backgroundColor: "#FFF176" }} />
              </span>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="grid grid-cols-4 gap-1">
              {HIGHLIGHT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHighlight({ color: c }).run();
                  }}
                />
              ))}
              <button
                type="button"
                title="Remove highlight"
                className="w-6 h-6 rounded border border-gray-200 text-gray-400 text-xs hover:bg-gray-100"
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetHighlight().run(); }}
              >✕</button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Text color */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Text colour"
              className="h-7 px-1.5 flex items-center gap-0.5 rounded text-gray-600 hover:bg-gray-100 text-xs font-bold"
            >
              <span className="relative font-bold">
                A
                <span className="absolute -bottom-0.5 left-0 right-0 h-1 rounded" style={{ backgroundColor: editor.getAttributes("textStyle").color ?? "#000000" }} />
              </span>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="grid grid-cols-4 gap-1">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().setColor(c).run();
                  }}
                />
              ))}
              <button
                type="button"
                title="Default colour"
                className="w-6 h-6 rounded border border-gray-200 text-gray-400 text-xs hover:bg-gray-100"
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetColor().run(); }}
              >✕</button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Link */}
        <ToolbarButton onClick={setLink} active={editor.isActive("link")} title="Link — select text first, then enter the URL. To open a link while editing, hold Ctrl and click it.">
          <Link2 className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Lists */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Indent / Outdent */}
        <ToolbarButton
          onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          title="Indent"
          active={false}
        >
          <Indent className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          title="Outdent"
          active={false}
        >
          <Outdent className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Image — camera & upload */}
        {imageUploading ? (
          <div className="h-7 w-7 flex items-center justify-center text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          </div>
        ) : (
          <>
            <ToolbarButton
              onClick={() => cameraInputRef.current?.click()}
              title="Take photo"
              active={false}
            >
              <Camera className="h-3.5 w-3.5" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => galleryInputRef.current?.click()}
              title="Insert image from file"
              active={false}
            >
              <ImagePlus className="h-3.5 w-3.5" />
            </ToolbarButton>
          </>
        )}

        {/* Context-specific toolbar items (e.g. Pull from program, Delivery status) */}
        {extraToolbarItems && (
          <>
            <div className="w-px h-4 bg-gray-200 mx-1" />
            {extraToolbarItems}
          </>
        )}
      </div>

      {/* Editor body — drop/paste of image files is handled by the
          ProseMirror plugin embedded in ImageWithUpload. The onDragOver
          here just allows drops to land on the editor area. */}
      <div onDragOver={(e) => e.preventDefault()}>
        <EditorContent editor={editor} />
      </div>

      {!editor.getText() && (
        <div className="absolute pointer-events-none top-[56px] left-4 text-gray-400 text-sm" style={{ display: editor.isFocused ? "none" : "" }}>
          {placeholder}
        </div>
      )}
    </div>
  );
}
